import { Injectable } from '@angular/core';
import { RouterState } from '@angular/router';
import {
  ApiClientCredential,
  BrandApiClientCredentialsConfigurationService,
  LocationApiClientCredentialsConfigurationService,
  RoomApiClientCredentialsConfigurationService,
} from '@era/settings-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, Observable, concat, of } from 'rxjs';
import { filter, first, map, mergeMap } from 'rxjs/operators';
import { Category } from '../router/category';
import { CategoryType } from '../shared/types';
import {
  CategoryApiClientSettingApiActions,
  CategoryApiClientSettingResolverActions,
  CategoryApiClientSettingViewActions,
} from './category-api-client-setting.actions';
import { selectApiClientCredentialLinkedToCategory } from './category-api-client-setting.selector';
import {
  ApiClientCredentialsConfiguration,
  apiClientCredentialsConfigurationFrom,
} from './category-api-client-setting.state';
import { safeMap } from '../effects.helpers';

@Injectable()
export class CategoryApiClientSettingEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly brandApiService: BrandApiClientCredentialsConfigurationService,
    private readonly locationApiService: LocationApiClientCredentialsConfigurationService,
    private readonly roomApiService: RoomApiClientCredentialsConfigurationService,
    private readonly routeStore: Store<RouterState>
  ) {}

  // Our API supports 0-N relations between categories and configurations. Our UI supports 0-1 relations. So we always pick
  // the first relation from the API when loading them.
  loadCategoryApiClientSettingEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryApiClientSettingResolverActions.categorySettingsRequested),
      mergeMap((action) =>
        safeMap(
          this.loadCategoryApiClientSetting(action.categoryType, action.categoryId).pipe(
            filter((configurations) => configurations.length > 0),
            map((configurations) => configurations[0])
          ),
          (configuration) => CategoryApiClientSettingApiActions.categorySettingLoaded({ configuration }),
          (error) => CategoryApiClientSettingApiActions.categorySettingLoadedError({ error })
        )
      )
    );
  });

  linkApiClientSettingToCategoryEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryApiClientSettingViewActions.settingSelected),
      mergeMap((action) => {
        const category: Category = {
          categoryType: action.categoryType,
          categoryId: action.categoryId,
          parent: undefined,
        };
        return this.routeStore.select(selectApiClientCredentialLinkedToCategory(category)).pipe(
          filter(
            (existingConfig) => (existingConfig ? existingConfig.id !== action.apiClientCredential.id : true) // has change?
          ),
          first(),
          mergeMap((existingConfig) => {
            // Delete the original setting
            const delete$: Observable<Action> = existingConfig
              ? this.delete$(action.categoryType, action.categoryId, {
                  type: action.categoryType,
                  id: action.categoryId,
                  apiClientCredentialsId: existingConfig.id,
                })
              : EMPTY;

            // Add the new setting
            const create$: Observable<Action> = this.create$(
              action.categoryType,
              action.categoryId,
              action.apiClientCredential
            );

            return concat(delete$, create$);
          })
        );
      })
    );
  });

  removeLinkedApiClientSettingFromCategoryEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryApiClientSettingViewActions.settingDeselected),
      mergeMap((action) => {
        const category: Category = {
          categoryType: action.categoryType,
          categoryId: action.categoryId,
          parent: undefined,
        };

        return this.routeStore.select(selectApiClientCredentialLinkedToCategory(category)).pipe(
          filter((existingConfig) => !!existingConfig),
          first(),
          mergeMap((existingConfig) =>
            this.delete$(category.categoryType, category.categoryId, {
              type: category.categoryType,
              id: category.categoryId,
              apiClientCredentialsId: existingConfig.id,
            })
          )
        );
      })
    );
  });

  private loadCategoryApiClientSetting(
    categoryType: CategoryType,
    categoryId: string
  ): Observable<ApiClientCredentialsConfiguration[]> {
    switch (categoryType) {
      case 'Brand':
        return this.brandApiService
          .getApiClientCredentialsConfigurationsForBrand(categoryId)
          .pipe(map((configurations) => configurations.map(apiClientCredentialsConfigurationFrom)));
      case 'Location':
        return this.locationApiService
          .getApiClientCredentialsConfigurationsForLocation(categoryId)
          .pipe(map((configurations) => configurations.map(apiClientCredentialsConfigurationFrom)));
      case 'Room':
        return this.roomApiService
          .getApiClientCredentialsForRoom(categoryId)
          .pipe(map((configurations) => configurations.map(apiClientCredentialsConfigurationFrom)));
      default:
        return of([]);
    }
  }

  private readonly create$ = (
    categoryType: CategoryType,
    categoryId: string,
    apiClientCredential: ApiClientCredential
  ): Observable<Action> => {
    switch (categoryType) {
      case 'Brand': {
        const brandConfiguration: ApiClientCredentialsConfiguration = {
          type: 'Brand',
          id: categoryId,
          apiClientCredentialsId: apiClientCredential.id,
        };

        return safeMap(
          this.brandApiService.addApiClientCredentialsToBrand(categoryId, {
            apiClientCredentialsId: apiClientCredential.id,
          }),
          (configuration) =>
            CategoryApiClientSettingApiActions.categorySettingAdded({
              configuration: apiClientCredentialsConfigurationFrom(configuration),
            }),
          (error) =>
            CategoryApiClientSettingApiActions.categorySettingAddedError({ configuration: brandConfiguration, error })
        );
      }
      case 'Location': {
        const locationConfiguration: ApiClientCredentialsConfiguration = {
          type: 'Location',
          id: categoryId,
          apiClientCredentialsId: apiClientCredential.id,
        };

        return safeMap(
          this.locationApiService.addApiClientCredentialsToLocation(categoryId, {
            apiClientCredentialsId: apiClientCredential.id,
          }),
          (configuration) =>
            CategoryApiClientSettingApiActions.categorySettingAdded({
              configuration: apiClientCredentialsConfigurationFrom(configuration),
            }),
          (error) =>
            CategoryApiClientSettingApiActions.categorySettingAddedError({
              configuration: locationConfiguration,
              error,
            })
        );
      }
      case 'Room': {
        const roomConfiguration: ApiClientCredentialsConfiguration = {
          type: 'Room',
          id: categoryId,
          apiClientCredentialsId: apiClientCredential.id,
        };

        return safeMap(
          this.roomApiService.addApiClientCredentialToRoom(categoryId, {
            apiClientCredentialsId: apiClientCredential.id,
          }),
          (configuration) =>
            CategoryApiClientSettingApiActions.categorySettingAdded({
              configuration: apiClientCredentialsConfigurationFrom(configuration),
            }),
          (error) =>
            CategoryApiClientSettingApiActions.categorySettingAddedError({ configuration: roomConfiguration, error })
        );
      }
      default:
        return EMPTY;
    }
  };

  private readonly delete$ = (
    categoryType: CategoryType,
    categoryId: string,
    configuration: ApiClientCredentialsConfiguration
  ): Observable<Action> => {
    switch (categoryType) {
      case 'Brand':
        return safeMap(
          this.brandApiService.deleteApiClientCredentialsFromBrand(categoryId),
          () => CategoryApiClientSettingApiActions.categorySettingDeleted({ configuration }),
          (error) => CategoryApiClientSettingApiActions.categorySettingDeletedError({ configuration, error })
        );
      case 'Location':
        return safeMap(
          this.locationApiService.deleteApiClientCredentialsFromLocation(categoryId),
          () => CategoryApiClientSettingApiActions.categorySettingDeleted({ configuration }),
          (error) => CategoryApiClientSettingApiActions.categorySettingDeletedError({ configuration, error })
        );
      case 'Room':
        return safeMap(
          this.roomApiService.deleteApiClientCredentialFromRoom(categoryId),
          () => CategoryApiClientSettingApiActions.categorySettingDeleted({ configuration }),
          (error) => CategoryApiClientSettingApiActions.categorySettingDeletedError({ configuration, error })
        );
      default:
        return EMPTY;
    }
  };
}
